import { useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { Loader2, AlertCircle, ArrowLeft, Video } from "lucide-react";
import { Button, Card, CardContent } from "@tellescope/ui";
import { useJoinVideoCall } from "../../hooks/useJoinVideoCall";

/**
 * Video call page for joining a Tellescope video call.
 * Maps to /video-call?calendarEventId={eventId}
 *
 * Flow:
 * 1. Fetch event using TanStack Query
 * 2. Show event details with "Join Video Call" button
 * 3. User clicks button to join
 * 4. VideoCallingProvider shows VideoCallDialog
 */
export const VideoCallPage = () => {
  const { t } = useTranslation("videoCalling");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { joinVideoCall, isJoining } = useJoinVideoCall();

  const calendarEventId = searchParams.get("calendarEventId") ?? "";

  // Fetch event using TanStack Query
  const {
    data: event,
    isLoading,
    error,
  } = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: calendarEventId,
    enabled: !!calendarEventId,
  });

  const handleJoinCall = useCallback(async () => {
    if (!event) return;
    await joinVideoCall(event);
  }, [event, joinVideoCall]);

  const handleBack = useCallback(() => {
    navigate("/");
  }, [navigate]);

  if (!calendarEventId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-sm font-medium text-destructive mb-1">{t("Missing event ID")}</p>
          <Button variant="outline" onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("Go Home")}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading...")}</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-sm font-medium text-destructive mb-1">{t("Unable to load event")}</p>
          <p className="text-xs text-muted-foreground mb-4">{error?.message ?? t("Event not found")}</p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("Go Home")}
          </Button>
        </div>
      </div>
    );
  }

  const displayTitle = event.displayTitle || event.title;

  return (
    <div className="flex items-center justify-center h-screen">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="p-6 text-center">
          <Video className="h-12 w-12 mx-auto mb-4 text-primary" />
          <h1 className="text-xl font-semibold mb-2">{displayTitle}</h1>
          <p className="text-sm text-muted-foreground mb-6">{t("Ready to join your video appointment")}</p>
          <Button className="w-full h-12" onClick={handleJoinCall} disabled={isJoining}>
            {isJoining ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {t("Joining meeting...")}
              </>
            ) : (
              <>
                <Video className="h-5 w-5 mr-2" />
                {t("Join Video Call")}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
